import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api } from '@/lib/api';
import type { BillableItem } from '@/lib/schemas';

interface ProposalPdfDownloadProps {
  projectId: string;
  projectName: string;
  documentPrefix?: string;
  hasProjectUnderstanding: boolean;
  hasFeeAssumptions: boolean;
  hasClientContact: boolean;
  billableItems: BillableItem[];
}

export function ProposalPdfDownload({
  projectId,
  projectName,
  documentPrefix,
  hasProjectUnderstanding,
  hasFeeAssumptions,
  hasClientContact,
  billableItems,
}: ProposalPdfDownloadProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);

  // Check for items that will be included in the PDF
  // Backend includes: proposed, proposed-optional, and confirmed items (excludes declined/cancelled)
  const includedStatuses = ['proposed', 'proposed-optional', 'confirmed'];
  const includedItems = billableItems.filter(
    item => includedStatuses.includes(item.item_status) && !item.cancelled
  );
  const hasIncludedItems = includedItems.length > 0;

  // Check for unexpected item statuses (items that will be excluded)
  const unexpectedItems = useMemo(() => {
    const statusCounts = new Map<string, number>();

    billableItems.forEach(item => {
      if (!item.cancelled && !includedStatuses.includes(item.item_status)) {
        const statusLabel = item.item_status === 'declined' ? 'Declined' :
                           item.item_status;
        statusCounts.set(
          statusLabel,
          (statusCounts.get(statusLabel) || 0) + 1
        );
      }
    });

    return Array.from(statusCounts.entries()).map(([status, count]) => ({
      status,
      count,
    }));
  }, [billableItems]);

  // Collect warnings for missing optional fields
  const warnings: string[] = [];
  if (!hasProjectUnderstanding) {
    warnings.push('Project Understanding is empty');
  }
  if (!hasFeeAssumptions) {
    warnings.push('Fee Assumptions is empty');
  }
  if (!hasClientContact) {
    warnings.push('No client contact set');
  }

  // Block if no includable items
  const canGenerate = hasIncludedItems;

  const handleDownload = async () => {
    setIsLoading(true);

    try {
      const blob = await api.projects.downloadProposalPDF(projectId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const docType = documentPrefix?.trim() ? `${documentPrefix.trim()} Fee Letter` : 'Fee Letter';
      a.download = `${projectName} - ${docType}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF Downloaded', {
        description: 'Proposal PDF has been generated and downloaded.',
      });
    } catch (error: any) {
      console.error('PDF download error:', error);
      toast.error('Download Failed', {
        description: error.message || 'Unknown error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadClick = () => {
    if (!canGenerate) {
      toast.error('Cannot generate PDF', {
        description: 'Add at least one fee item to generate a proposal PDF.',
      });
      return;
    }

    // Show warning if there are unexpected statuses
    if (unexpectedItems.length > 0) {
      setShowWarningDialog(true);
    } else {
      handleDownload();
    }
  };

  const handleConfirmDownload = () => {
    setShowWarningDialog(false);
    handleDownload();
  };

  return (
    <>
      <div className="flex items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleDownloadClick}
                disabled={!canGenerate || isLoading}
                variant={warnings.length > 0 ? 'outline' : 'default'}
                data-testid="download-proposal-pdf-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download Fee Letter
                  </>
                )}
              </Button>
            </TooltipTrigger>
            {!canGenerate && (
              <TooltipContent>
                <p>Add fee items to generate PDF</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>

        {warnings.length > 0 && canGenerate && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <AlertTriangle
                  className="h-4 w-4 text-yellow-500"
                  data-testid="missing-fields-warning"
                  aria-label="Missing optional fields"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium mb-1">Missing optional fields:</p>
                <ul className="list-disc pl-4">
                  {warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
                <p className="text-xs mt-1 text-muted-foreground">
                  PDF will generate but sections will be empty
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Item Status Warning Dialog */}
      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Unexpected Item Statuses
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  This proposal PDF will only include items with status &quot;Proposed&quot;, &quot;Proposed (Optional)&quot;, or &quot;Confirmed&quot;.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <p className="font-medium text-yellow-900 mb-2">
                    The following items will NOT be included:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-yellow-800">
                    {unexpectedItems.map((item, i) => (
                      <li key={i}>
                        {item.count} item{item.count > 1 ? 's' : ''} with status &quot;{item.status}&quot;
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-sm text-gray-600">
                  To include these items, update their status to &quot;Proposed&quot; before generating the PDF.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDownload}>
              Generate Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
